import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { loadRuntimeConfig } from "@/lib/config";
import { AuthError } from "./authError";
import { readCognitoRole, type CognitoRole } from "./jwt";
import {
  clearTokens,
  isTokenValid,
  readAndClearFlash,
  readTokens,
  writeFlash,
  writeTokens,
  type StoredTokens,
} from "./session";
import { hasPendingPasswordReset } from "./passwordResetLink";
import type { RuntimeConfig } from "@/types";

type AuthStatus = "loading" | "unauthenticated" | "authenticated" | "config-error";

type SignInResult = "signed-in" | "confirm-sign-up" | "new-password-required" | "reset-password";
type SignUpResult = "signed-in" | "confirm-sign-up";

interface AuthContextValue {
  status: AuthStatus;
  config: RuntimeConfig | null;
  configError: string | null;
  authError: string | null;
  flash: string | null;
  email: string | null;
  role: CognitoRole;
  accessToken: string | null;
  invalidateSession: (reason: string) => void;
  dismissAuthError: () => void;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signUp: (email: string, password: string) => Promise<SignUpResult>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendSignUpCode: (email: string) => Promise<void>;
  beginPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (token: string, newPassword: string) => Promise<void>;
  confirmNewPassword: (newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function errorMessage(error: unknown): string {
  return error instanceof AuthError ? error.message : "We couldn't complete that request. Please try again.";
}

// Cognito is only needed after an auth action or an expired session; a static import would
// add its browser SDK to every public route's startup bundle.
function loadAuthClient() {
  return import("./authClient");
}

function revokeSession(config: RuntimeConfig, tokens: StoredTokens | null): void {
  void loadAuthClient()
    .then(({ signOut }) => signOut(config, tokens))
    .catch(() => undefined);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [tokens, setTokens] = useState<StoredTokens | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    void (async () => {
      let loadedConfig: RuntimeConfig;
      try {
        loadedConfig = await loadRuntimeConfig();
      } catch (error) {
        setConfigError(error instanceof Error ? error.message : "Configuration failed to load.");
        setStatus("config-error");
        return;
      }
      setConfig(loadedConfig);
      if (hasPendingPasswordReset()) {
        clearTokens();
        readAndClearFlash();
        setStatus("unauthenticated");
        return;
      }

      const message = readAndClearFlash();
      if (message) setFlash(message);

      const stored = readTokens();
      if (!stored) {
        setStatus("unauthenticated");
        return;
      }
      if (isTokenValid(stored)) {
        setTokens(stored);
        setStatus("authenticated");
        return;
      }

      try {
        const authClient = await loadAuthClient();
        const refreshed = await authClient.refreshSession(loadedConfig, stored);
        writeTokens(refreshed);
        setTokens(refreshed);
        setStatus("authenticated");
      } catch {
        clearTokens();
        setTokens(null);
        setFlash("Your session ended. Please sign in again.");
        setStatus("unauthenticated");
        revokeSession(loadedConfig, stored);
      }
    })();
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    function establishSession(nextTokens: StoredTokens) {
      writeTokens(nextTokens);
      setTokens(nextTokens);
      setAuthError(null);
      setFlash(null);
      setStatus("authenticated");
    }

    function requireConfig(): RuntimeConfig {
      if (!config) throw new AuthError("Configuration is still loading. Please try again.");
      return config;
    }

    async function signIn(email: string, password: string): Promise<SignInResult> {
      try {
        const authClient = await loadAuthClient();
        const result = await authClient.signIn(requireConfig(), email, password);
        if (result.tokens) establishSession(result.tokens);
        return result.step;
      } catch (error) {
        setAuthError(errorMessage(error));
        throw error;
      }
    }

    async function signUp(email: string, password: string): Promise<SignUpResult> {
      try {
        const authClient = await loadAuthClient();
        const result = await authClient.signUp(requireConfig(), email, password);
        if (result.tokens) establishSession(result.tokens);
        return result.step;
      } catch (error) {
        setAuthError(errorMessage(error));
        throw error;
      }
    }

    async function confirmSignUp(email: string, code: string): Promise<void> {
      try {
        const authClient = await loadAuthClient();
        await authClient.confirmSignUp(requireConfig(), email, code);
        setAuthError(null);
      } catch (error) {
        setAuthError(errorMessage(error));
        throw error;
      }
    }

    async function resendSignUpCode(email: string): Promise<void> {
      try {
        const authClient = await loadAuthClient();
        await authClient.resendSignUpCode(requireConfig(), email);
        setAuthError(null);
      } catch (error) {
        setAuthError(errorMessage(error));
        throw error;
      }
    }

    async function beginPasswordReset(email: string): Promise<void> {
      try {
        const authClient = await loadAuthClient();
        await authClient.beginPasswordReset(email);
        setAuthError(null);
      } catch (error) {
        setAuthError(errorMessage(error));
        throw error;
      }
    }

    async function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
      try {
        const authClient = await loadAuthClient();
        await authClient.confirmPasswordReset(token, newPassword);
        setAuthError(null);
      } catch (error) {
        setAuthError(errorMessage(error));
        throw error;
      }
    }

    async function confirmNewPassword(newPassword: string): Promise<void> {
      try {
        const authClient = await loadAuthClient();
        establishSession(await authClient.confirmNewPassword(requireConfig(), newPassword));
      } catch (error) {
        setAuthError(errorMessage(error));
        throw error;
      }
    }

    async function signOut(): Promise<void> {
      const activeTokens = tokens;
      clearTokens();
      setTokens(null);
      setAuthError(null);
      setStatus("unauthenticated");
      if (config) {
        const authClient = await loadAuthClient();
        await authClient.signOut(config, activeTokens);
      }
    }

    function invalidateSession(reason: string): void {
      const activeTokens = tokens;
      clearTokens();
      setTokens(null);
      writeFlash(reason);
      setFlash(reason);
      setAuthError(null);
      setStatus("unauthenticated");
      if (config) revokeSession(config, activeTokens);
    }

    return {
      status,
      config,
      configError,
      authError,
      flash,
      email: tokens?.email ?? null,
      role: readCognitoRole(isTokenValid(tokens) ? tokens.accessToken : null),
      accessToken: isTokenValid(tokens) ? tokens.accessToken : null,
      invalidateSession,
      dismissAuthError: () => setAuthError(null),
      signIn,
      signUp,
      confirmSignUp,
      resendSignUpCode,
      beginPasswordReset,
      confirmPasswordReset,
      confirmNewPassword,
      signOut,
    };
  }, [authError, config, configError, flash, status, tokens]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
